import React, { useEffect, useState } from 'react';
import { Menu, X, Search, Mountain, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from './ui/Button';
import { SearchOverlay } from './SearchOverlay';
import { motion, AnimatePresence } from 'framer-motion';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const destinations = [{
    name: 'Nepal',
    href: '#destination-nepal',
    subItems: [{
      name: 'Trekking in Nepal',
      href: '#everest-region-treks'
    }, {
      name: 'Cultural Tours',
      href: '#'
    }, {
      name: 'Family Holidays',
      href: '#family-holidays'
    }, {
      name: 'Spiritual Tours',
      href: '#spiritual-journeys'
    }, {
      name: 'Honeymoon Tours',
      href: '#'
    }, {
      name: 'Adventure Tours',
      href: '#'
    }, {
      name: 'Wildlife Safari',
      href: '#'
    }, {
      name: 'Helicopter Tours',
      href: '#'
    }, {
      name: 'Luxury Treks',
      href: '#'
    }]
  }, {
    name: 'Tibet',
    href: '#'
  }, {
    name: 'Bhutan',
    href: '#'
  }];
  const experiences = [{
    name: 'Family Holidays',
    href: '#family-holidays'
  }];
  const trekking = [{
    name: 'Everest Region Treks',
    href: '#everest-region-treks'
  }];
  const guides = [{
    name: 'Everest Trek Guide',
    href: '#everest-trek-guide'
  }];
  const aboutUs = [{
    name: 'Our Team',
    href: '#our-team'
  }, {
    name: 'Why Choose Us',
    href: '#why-choose-us'
  }, {
    name: 'Legal Documents',
    href: '#legal-documents'
  }];
  const navLinks = [{
    name: 'Contact',
    href: '#contact'
  }];
  return <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md shadow-md py-3' : 'bg-transparent py-5'}`} data-id="element-176">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-177">
          <div className="flex justify-between items-center" data-id="element-178">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2" data-id="element-179">
              <div className="bg-gradient-to-br from-[#0066FF] to-[#06B6D4] p-2 rounded-lg text-white" data-id="element-180">
                <Mountain size={24} data-id="element-181" />
              </div>
              <span className={`text-xl font-bold tracking-tight ${isScrolled ? 'text-gray-900' : 'text-white'}`} data-id="element-182">
                Himalayan<span className="text-[#0066FF]" data-id="element-183">Trekking</span>
              </span>
            </a>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8" data-id="element-184">
              {/* Destination Mega Menu */}
              <div className="relative" onMouseEnter={() => setActiveDropdown('destination')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-185">
                <button className={`text-sm font-medium hover:text-[#0066FF] transition-colors flex items-center gap-1 ${isScrolled ? 'text-gray-700' : 'text-white/90 hover:text-white'}`} data-id="element-186">
                  Destination
                  <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'destination' ? 'rotate-180' : ''}`} data-id="element-187" />
                </button>

                <AnimatePresence data-id="element-188">
                  {activeDropdown === 'destination' && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden" style={{
                  minWidth: '600px'
                }} data-id="element-189">
                      <div className="grid grid-cols-3 divide-x divide-gray-100" data-id="element-190">
                        {/* Nepal Column with Mega Menu */}
                        <div className="col-span-2 p-4" data-id="element-191">
                          <a href={destinations[0].href} className="block px-4 py-3 text-base font-bold text-gray-900 hover:text-[#0066FF] transition-colors mb-2" data-id="element-192">
                            {destinations[0].name}
                          </a>
                          <div className="grid grid-cols-2 gap-1 pl-4" data-id="element-193">
                            {destinations[0].subItems?.map(item => <a key={item.name} href={item.href} className="flex items-center gap-2 px-3 py-2.5 text-sm text-gray-600 hover:text-[#0066FF] hover:bg-blue-50 rounded-lg transition-colors group" data-id="element-194">
                                <ChevronRight size={14} className="text-gray-400 group-hover:text-[#0066FF] transition-colors" data-id="element-195" />
                                {item.name}
                              </a>)}
                          </div>
                        </div>

                        {/* Other Destinations Column with Background */}
                        <div className="p-4 bg-gradient-to-br from-stone-50 to-stone-100" data-id="element-196">
                          {destinations.slice(1).map(dest => <a key={dest.name} href={dest.href} className="block px-4 py-3 text-sm text-gray-700 hover:text-[#0066FF] hover:bg-white rounded-lg transition-colors font-medium" data-id="element-197">
                              {dest.name}
                            </a>)}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Experiences Dropdown */}
              <div className="relative" onMouseEnter={() => setActiveDropdown('experiences')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-198">
                <button className={`text-sm font-medium hover:text-[#0066FF] transition-colors flex items-center gap-1 ${isScrolled ? 'text-gray-700' : 'text-white/90 hover:text-white'}`} data-id="element-199">
                  Experiences
                  <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'experiences' ? 'rotate-180' : ''}`} data-id="element-200" />
                </button>

                <AnimatePresence data-id="element-201">
                  {activeDropdown === 'experiences' && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden" data-id="element-202">
                      {experiences.map(exp => <a key={exp.name} href={exp.href} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0066FF] transition-colors" data-id="element-203">
                          {exp.name}
                        </a>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Trekking Dropdown */}
              <div className="relative" onMouseEnter={() => setActiveDropdown('trekking')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-204">
                <button className={`text-sm font-medium hover:text-[#0066FF] transition-colors flex items-center gap-1 ${isScrolled ? 'text-gray-700' : 'text-white/90 hover:text-white'}`} data-id="element-205">
                  Trekking
                  <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'trekking' ? 'rotate-180' : ''}`} data-id="element-206" />
                </button>

                <AnimatePresence data-id="element-207">
                  {activeDropdown === 'trekking' && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden" data-id="element-208">
                      {trekking.map(trek => <a key={trek.name} href={trek.href} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0066FF] transition-colors" data-id="element-209">
                          {trek.name}
                        </a>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Guide Dropdown */}
              <div className="relative" onMouseEnter={() => setActiveDropdown('guide')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-210">
                <button className={`text-sm font-medium hover:text-[#0066FF] transition-colors flex items-center gap-1 ${isScrolled ? 'text-gray-700' : 'text-white/90 hover:text-white'}`} data-id="element-211">
                  Guide
                  <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'guide' ? 'rotate-180' : ''}`} data-id="element-212" />
                </button>

                <AnimatePresence data-id="element-213">
                  {activeDropdown === 'guide' && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden" data-id="element-214">
                      {guides.map(guide => <a key={guide.name} href={guide.href} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0066FF] transition-colors" data-id="element-215">
                          {guide.name}
                        </a>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* About Us Dropdown */}
              <div className="relative" onMouseEnter={() => setActiveDropdown('about')} onMouseLeave={() => setActiveDropdown(null)} data-id="element-216">
                <button className={`text-sm font-medium hover:text-[#0066FF] transition-colors flex items-center gap-1 ${isScrolled ? 'text-gray-700' : 'text-white/90 hover:text-white'}`} data-id="element-217">
                  About Us
                  <ChevronDown size={16} className={`transition-transform ${activeDropdown === 'about' ? 'rotate-180' : ''}`} data-id="element-218" />
                </button>

                <AnimatePresence data-id="element-219">
                  {activeDropdown === 'about' && <motion.div initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} transition={{
                  duration: 0.2
                }} className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 overflow-hidden" data-id="element-220">
                      {aboutUs.map(item => <a key={item.name} href={item.href} className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-[#0066FF] transition-colors" data-id="element-221">
                          {item.name}
                        </a>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Regular Links */}
              {navLinks.map(link => <a key={link.name} href={link.href} className={`text-sm font-medium hover:text-[#0066FF] transition-colors ${isScrolled ? 'text-gray-700' : 'text-white/90 hover:text-white'}`} data-id="element-222">
                  {link.name}
                </a>)}

              <button onClick={() => setIsSearchOpen(true)} className={`p-2 rounded-full transition-colors ${isScrolled ? 'text-gray-600 hover:bg-gray-100' : 'text-white hover:bg-white/10'}`} data-id="element-223">
                <Search size={20} data-id="element-224" />
              </button>
              <a href="#plan-trip" data-id="element-225">
                <Button variant="primary" size="sm" data-id="element-226">
                  Plan a Trip
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-2" data-id="element-227">
              <button onClick={() => setIsSearchOpen(true)} className={`p-2 rounded-md ${isScrolled ? 'text-gray-900' : 'text-white'}`} data-id="element-228">
                <Search size={20} data-id="element-229" />
              </button>
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`p-2 rounded-md ${isScrolled ? 'text-gray-900' : 'text-white'}`} data-id="element-230">
                {isMobileMenuOpen ? <X size={24} data-id="element-231" /> : <Menu size={24} data-id="element-232" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence data-id="element-233">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="md:hidden bg-white border-t" data-id="element-234">
              <div className="px-4 pt-2 pb-6 space-y-2 max-h-[70vh] overflow-y-auto" data-id="element-235">
                {/* Mobile Destination */}
                <div className="space-y-1" data-id="element-236">
                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase" data-id="element-237">
                    Destination
                  </p>
                  <a href={destinations[0].href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-bold text-gray-900 hover:text-[#0066FF] hover:bg-gray-50 rounded-md" data-id="element-238">
                    {destinations[0].name}
                  </a>
                  <div className="pl-4 space-y-1" data-id="element-239">
                    {destinations[0].subItems?.map(item => <a key={item.name} href={item.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-sm text-gray-600 hover:text-[#0066FF] hover:bg-gray-50 rounded-md" data-id="element-240">
                        {item.name}
                      </a>)}
                  </div>
                  {destinations.slice(1).map(dest => <a key={dest.name} href={dest.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md" data-id="element-241">
                      {dest.name}
                    </a>)}
                </div>

                {/* Mobile Experiences */}
                <div className="space-y-1 pt-2" data-id="element-242">
                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase" data-id="element-243">
                    Experiences
                  </p>
                  {experiences.map(exp => <a key={exp.name} href={exp.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md" data-id="element-244">
                      {exp.name}
                    </a>)}
                </div>

                {/* Mobile Trekking */}
                <div className="space-y-1 pt-2" data-id="element-245">
                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase" data-id="element-246">
                    Trekking
                  </p>
                  {trekking.map(trek => <a key={trek.name} href={trek.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md" data-id="element-247">
                      {trek.name}
                    </a>)}
                </div>

                {/* Mobile Guide */}
                <div className="space-y-1 pt-2" data-id="element-248">
                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase" data-id="element-249">
                    Guide
                  </p>
                  {guides.map(guide => <a key={guide.name} href={guide.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md" data-id="element-250">
                      {guide.name}
                    </a>)}
                </div>

                {/* Mobile About Us */}
                <div className="space-y-1 pt-2" data-id="element-251">
                  <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase" data-id="element-252">
                    About Us
                  </p>
                  {aboutUs.map(item => <a key={item.name} href={item.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md" data-id="element-253">
                      {item.name}
                    </a>)}
                </div>

                {/* Mobile Regular Links */}
                <div className="pt-2 border-t border-gray-200" data-id="element-254">
                  {navLinks.map(link => <a key={link.name} href={link.href} onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-3 text-base font-medium text-gray-700 hover:text-[#0066FF] hover:bg-gray-50 rounded-md" data-id="element-255">
                      {link.name}
                    </a>)}
                </div>

                <div className="pt-4 flex flex-col gap-3" data-id="element-256">
                  <a href="#plan-trip" onClick={() => setIsMobileMenuOpen(false)} data-id="element-257">
                    <Button variant="primary" className="w-full justify-center" data-id="element-258">
                      Plan a Trip
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      {/* Search Overlay */}
      <SearchOverlay isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} data-id="element-259" />
    </>;
}